import { IDXFRenderContext } from '../floor-plan/dxf/types';
import BaseEntity from './BaseEntity';
import { FloorPlanDxfModel } from './2d/floorPlanDxfModel';
import { Box3 } from 'three';
import { pixelRatio2d } from '../math/const';
import { ENTITY_RENDER_ORDER } from '../constant/entity';
import { EEntityType } from '../types';
import { IPoint2d } from '../math/IPoint2d';

/**
 * Represents a floor plan entity.
 */
export class FloorPlanDxfEntity extends BaseEntity<EEntityType.FloorPlan> {
  protected override _model2d = new FloorPlanDxfModel();
  private _context: IDXFRenderContext | null = null;
  private _hiddenLayers: Set<string> = new Set();
  private _floorPlanType = 'dxf';

  constructor() {
    super({ type: EEntityType.FloorPlan });

    this._model2d.setDisplayOrder(ENTITY_RENDER_ORDER.floorPlanDXF);
  }

  get floorPlanType() {
    return this._floorPlanType;
  }

  /**
   * 重写获取包围盒方法
   */
  override getBoundingBox(): Box3 {
    const box = new Box3();
    if (this._context) {
      const { minX, minY, maxX, maxY } = this._context.bounding;
      box.setFromArray([minX / pixelRatio2d, minY / pixelRatio2d, 0, maxX / pixelRatio2d, maxY / pixelRatio2d, 0]);
    }
    return box;
  }

  getContext() {
    return this._context;
  }

  getVisible() {
    return this._visible;
  }

  getHiddenLayers() {
    return [...this._hiddenLayers];
  }

  getLayerAtPoint(mousePos: IPoint2d): string | null {
    return this._model2d.getLayerAtPoint(mousePos);
  }

  /** 高亮指定 layer 的线条，null 表示取消高亮 */
  setLayerHighlight(layerName: string | null): void {
    this._model2d.setLayerHighlight(layerName);
  }

  setVisible(visible: boolean) {
    this._visible = visible;
    this._model2d.setVisible(visible);
  }

  /**
   * 批量设置多个 layer 的显隐状态，只触发一次渲染刷新和一次 QuadTree 排除索引重建
   */
  setLayersVisible(updates: Record<string, boolean>) {
    if (!this._context) return;

    for (const [layerName, visible] of Object.entries(updates)) {
      if (visible) {
        this._hiddenLayers.delete(layerName);
      } else {
        this._hiddenLayers.add(layerName);
      }
    }

    // 批量更新渲染网格（内部只触发一次文字刷新）
    this._model2d.setLayersVisible(updates);

    // 重建排除索引集合并同步到 QuadTree（只执行一次）
    const excluded = new Set<number>();
    for (const hiddenLayer of this._hiddenLayers) {
      const indices = this._context.layerLineIndices[hiddenLayer];
      if (indices) {
        for (const idx of indices) excluded.add(idx);
      }
    }
    this._context.quadTree.setExcludedIndices(excluded);
  }

  setContext(context: IDXFRenderContext) {
    if (this._context) {
      this._context.quadTree?.clear();
    }
    this._context = context;
    this._hiddenLayers.clear();
  }

  async render() {
    await this._model2d.render(this._context);
  }

  override dispose() {
    super.dispose();
    this._context?.quadTree?.clear();
    this._context = null;
  }
}
